import React, { useContext, useState, useRef, useEffect } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { ThemeContext } from '../context/ThemeContext';
import { AuthContext } from '../context/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import {
  hasFullAccess,
  isBranchAdmin,
  hasBranchAdminAccess,
  canCreateUsers,
  canAccessManageRestaurant,
  canAccessBranches,
  canAccessPOS,
  canAccessQRMenu,
  isChef,
  isWaiter
} from '../utils/permissionHelpers';
import {
  FaHome,
  FaUtensils,
  FaStore,
  FaClipboardList,
  FaUsers,
  FaChair,
  FaCog,
  FaChevronLeft,
  FaChevronRight,
  FaCashRegister,
  FaPalette,
  FaUser,
  FaSignOutAlt,
  FaQrcode,
  FaReceipt,
  FaDownload,
  FaFileInvoice,
  FaShieldAlt,
  FaComments
} from 'react-icons/fa';

const isElectronApp = () =>
  typeof window !== 'undefined' && !!window.electronAPI?.isElectron;

/** Sidebar paths hidden in the desktop POS app (configure in web dashboard). */
const ELECTRON_HIDDEN_NAV_PATHS = new Set([
  '/branches',
  '/settings',
  '/settings/themes',
]);

const filterNavItemsForElectron = (items) => {
  if (!isElectronApp()) return items;
  return items.filter((item) => !ELECTRON_HIDDEN_NAV_PATHS.has(item.path));
};

// Keyframe animations
const pulse = keyframes`
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
`;
const SidebarContainer = styled(motion.div)`
  background: ${props => props.isExpanded
    ? 'rgba(15, 15, 15, 0.95)'
    : 'linear-gradient(180deg, rgba(15, 15, 15, 0.98) 0%, rgba(20, 20, 20, 0.96) 100%)'};
  backdrop-filter: blur(30px);
  -webkit-backdrop-filter: blur(30px);
  border-right: ${props => props.isExpanded
    ? '1px solid var(--card-border)'
    : '1px solid var(--primary-rgba-20)'};
  height: 100%;
  max-height: 100%;
  box-shadow: ${props => props.isExpanded
    ? '2px 0 20px rgba(0, 0, 0, 0.5)'
    : '4px 0 32px rgba(0, 0, 0, 0.6), inset -1px 0 1px rgba(255, 255, 255, 0.03)'};
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  transition: all ${props => props.theme?.transitions?.medium || '0.3s'} cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 10;
  position: relative;

  /* Subtle gradient overlay when collapsed */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, var(--primary-rgba-5) 0%, transparent 50%, var(--primary-rgba-3) 100%);
    opacity: ${props => props.isExpanded ? 0 : 1};
    transition: opacity 0.3s ease;
    pointer-events: none;
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }
  
  /* Desktop — viewport-tall sticky rail so nav can scroll independently */
  @media (min-width: 1025px) {
    position: sticky;
    top: 0;
    height: 100vh;
    max-height: 100vh;
    height: 100dvh;
    max-height: 100dvh;
  }
  
  /* Tablet - fixed positioning right after header */
  @media (min-width: 769px) and (max-width: 1024px) {
    position: sticky;
    top: 0;
    left: 0;
    padding-top: ${props => (props.isAtPageTop ? 'calc(64px + env(safe-area-inset-top, 0px))' : '0')};
    bottom: 0;
    width: ${props => props.isExpanded ? '230px' : '70px'};
    height: 100vh;
    max-height: 100vh;
    height: 100dvh;
    max-height: 100dvh;
    z-index: 100;
    box-shadow: ${props => props.theme?.name === 'light'
      ? `2px 0 20px rgba(0, 0, 0, 0.15)`
      : `2px 0 20px rgba(0, 0, 0, 0.4)`
    };
  }
  
  /* Mobile - fixed positioning right after header.
     The header is ~60px tall (min-height 52-60 + padding, height auto) and
     sits below the phone's status bar / notch,
     so the safe-area inset must be added or the first nav item hides
     underneath the header. */
  @media (max-width: 768px) {
    position: sticky;
    padding-top: calc(64px + env(safe-area-inset-top, 0px));
    top: 0;
    left: 0;
    bottom: 0;
    height: 100vh;
    max-height: 100vh;
    height: 100dvh;
    max-height: 100dvh;
    z-index: 100;
    box-shadow: ${props => props.theme?.name === 'light'
      ? `2px 0 20px rgba(0, 0, 0, 0.2)`
      : `2px 0 20px rgba(0, 0, 0, 0.5)`
    };
  }

`;

const NavGroupsLock = styled.div`
  opacity: ${props => props.disabled ? 0.45 : 1};
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};
  filter: ${props => props.disabled ? 'grayscale(0.4)' : 'none'};
  transition: opacity 0.2s ease, filter 0.2s ease;
`;

const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${props => props.isExpanded ? 'space-between' : 'center'};
  padding: ${props => props.isExpanded ? '1.5rem 1.25rem' : '1.5rem 0'};
  border-bottom: 1px solid ${props => props.isExpanded ? 'var(--card-border)' : 'var(--primary-rgba-15)'};
  position: relative;
  background: ${props => props.isExpanded
    ? 'transparent'
    : 'linear-gradient(180deg, var(--primary-rgba-5) 0%, transparent 100%)'};
  transition: all 0.3s ease;

  /* Subtle shine effect when collapsed */
  ${props => !props.isExpanded && `
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 60%;
      height: 1px;
      background: linear-gradient(90deg, transparent 0%, var(--primary-rgba-30) 50%, transparent 100%);
    }
  `}
`;

const ToggleButton = styled(motion.button)`
  background: var(--primary-rgba-10);
  border: 1px solid transparent;
  color: var(--primary-color);
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  width: 36px;
  height: 36px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 12px var(--primary-rgba-10), inset 0 1px 0 rgba(255, 255, 255, 0.1);

  /* Shine effect */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, transparent 0%, var(--primary-rgba-20) 50%, transparent 100%);
    transform: translateX(-100%);
    transition: transform 0.6s ease;
  }

  &:hover {
    background: var(--primary-rgba-15);
    transform: scale(1.08) rotate(5deg);
    box-shadow: 0 6px 20px var(--primary-rgba-20), inset 0 1px 0 rgba(255, 255, 255, 0.15);
    border-color: var(--primary-rgba-30);

    &::after {
      transform: translateX(100%);
    }
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    font-size: 0.9rem;
    position: relative;
    z-index: 1;
    transition: transform 0.3s ease;
  }
`;

const NavSection = styled.div`
  margin-top: 0.5rem;
  padding: ${props => props.isExpanded ? '0 0.75rem' : '0'};
`;

const NavSectionTitle = styled(motion.div)`
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  margin: 1.5rem 0 0.75rem;
  padding: ${props => props.isExpanded ? '0 0.75rem' : '0'};
  white-space: nowrap;
  opacity: ${props => props.isExpanded ? 1 : 0};
  transition: opacity 0.3s ease;
  text-align: ${props => props.isExpanded ? 'left' : 'center'};
`;

const NavItemWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const NavItemTooltip = styled(motion.div)`
  position: absolute;
  left: calc(100% + 1rem);
  top: 50%;
  transform: translateY(-50%);
  background: linear-gradient(135deg, rgba(20, 20, 20, 0.98) 0%, rgba(15, 15, 15, 0.98) 100%);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  color: var(--text-primary-dark);
  padding: 0.5rem 0.875rem;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 600;
  white-space: nowrap;
  z-index: 1000;
  pointer-events: none;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5),
              0 0 0 1px var(--primary-rgba-20),
              inset 0 1px 0 rgba(255, 255, 255, 0.1);
  border: 1px solid var(--primary-rgba-20);

  /* Arrow pointing left */
  &::before {
    content: '';
    position: absolute;
    right: 100%;
    top: 50%;
    transform: translateY(-50%);
    border: 6px solid transparent;
    border-right-color: var(--primary-rgba-20);
  }

  &::after {
    content: '';
    position: absolute;
    right: 100%;
    top: 50%;
    transform: translateY(-50%);
    border: 5px solid transparent;
    border-right-color: rgba(20, 20, 20, 0.98);
    margin-right: 1px;
  }
`;

const tooltipVariants = {
  hidden: {
    opacity: 0,
    x: -10,
    transition: {
      duration: 0.2
    }
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1]
    }
  }
};

const NavItem = styled(motion(NavLink))`
  display: flex;
  align-items: center;
  padding: ${props => props.isExpanded ? '0.875rem 1rem' : '0.875rem 0'};
  color: rgba(255, 255, 255, 0.95);
  text-decoration: none;
  border-radius: ${props => props.isExpanded ? 'var(--radius-md)' : '12px'};
  margin-bottom: ${props => props.isExpanded ? '0.375rem' : '0.5rem'};
  position: relative;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  justify-content: ${props => props.isExpanded ? 'flex-start' : 'center'};
  font-weight: 500;
  font-size: 0.95rem;
  width: ${props => props.isExpanded ? 'auto' : '44px'};
  height: ${props => props.isExpanded ? 'auto' : '44px'};
  margin-left: ${props => props.isExpanded ? '0' : 'auto'};
  margin-right: ${props => props.isExpanded ? '0' : 'auto'};
  overflow: visible;

  /* Enhanced glow when collapsed */
  ${props => !props.isExpanded && `
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.05);

    &::before {
      content: '';
      position: absolute;
      inset: -1px;
      background: linear-gradient(135deg, var(--primary-rgba-10) 0%, transparent 50%, var(--primary-rgba-5) 100%);
      border-radius: 12px;
      opacity: 0;
      transition: opacity 0.3s ease;
      z-index: -1;
    }
  `}

  svg {
    font-size: ${props => props.isExpanded ? '1.2rem' : '1.3rem'};
    min-width: ${props => props.isExpanded ? '1.25rem' : 'auto'};
    margin-right: ${props => props.isExpanded ? '0.875rem' : '0'};
    transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    color: inherit;
    filter: ${props => props.isExpanded ? 'none' : 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'};
  }

  span {
    white-space: nowrap;
    opacity: ${props => props.isExpanded ? 1 : 0};
    max-width: ${props => props.isExpanded ? '200px' : '0'};
    overflow: hidden;
    transition: opacity 0.3s ease, max-width 0.3s ease;
    font-weight: 500;
  }

  &:hover {
    background: var(--primary-rgba-10);
    color: rgba(255, 255, 255, 0.95);
    transform: ${props => props.isExpanded ? 'none' : 'scale(1.1)'};
    box-shadow: ${props => props.isExpanded
      ? 'none'
      : '0 4px 16px var(--primary-rgba-15), 0 0 0 2px var(--primary-rgba-20), inset 0 1px 0 rgba(255, 255, 255, 0.1)'};

    svg {
      transform: scale(1.15);
      color: rgba(255, 255, 255, 0.95);
      filter: drop-shadow(0 2px 8px var(--primary-rgba-30));
    }

    ${props => !props.isExpanded && `
      &::before {
        opacity: 1;
      }
    `}
  }

  &.active {
    background: ${props => props.isExpanded
      ? 'var(--primary-rgba-15)'
      : 'linear-gradient(135deg, var(--primary-rgba-20) 0%, var(--primary-rgba-15) 100%)'};
    color: rgba(255, 255, 255, 0.95);
    font-weight: 600;
    box-shadow: ${props => props.isExpanded
      ? 'none'
      : '0 4px 16px var(--primary-rgba-20), 0 0 0 2px var(--primary-rgba-25), inset 0 1px 0 rgba(255, 255, 255, 0.15)'};

    svg {
      color: rgba(255, 255, 255, 0.95);
      transform: ${props => props.isExpanded ? 'none' : 'scale(1.05)'};
      filter: drop-shadow(0 2px 8px var(--primary-rgba-40));
    }

    /* Right-side border accent for desktop when expanded */
    ${props => props.isExpanded && `
      @media (min-width: 769px) {
        &::after {
          content: '';
          position: absolute;
          right: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 3px;
          height: 60%;
          background: var(--primary-color);
          border-radius: 2px 0 0 2px;
          box-shadow: 0 0 8px var(--primary-rgba-40);
        }
      }
    `}

    /* Pulsing glow when active and collapsed */
    ${props => !props.isExpanded && css`
      &::before {
        opacity: 1;
        animation: ${pulse} 2s ease-in-out infinite;
      }
    `}
  }
`;

const SidebarFooter = styled.div`
  margin-top: auto;
  padding: ${props => props.isExpanded ? '1rem 0.75rem' : '1rem 0'};
  border-top: 1px solid ${props => props.isExpanded ? 'var(--card-border)' : 'var(--primary-rgba-15)'};
  position: relative;
  background: ${props => props.isExpanded
    ? 'transparent'
    : 'linear-gradient(0deg, var(--primary-rgba-5) 0%, transparent 100%)'};
  transition: all 0.3s ease;

  /* Subtle shine effect when collapsed */
  ${props => !props.isExpanded && `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 60%;
      height: 1px;
      background: linear-gradient(90deg, transparent 0%, var(--primary-rgba-30) 50%, transparent 100%);
    }
  `}
`;

const UserSection = styled.div`
  position: relative;
  width: ${props => props.isExpanded ? '100%' : '44px'};
  margin: ${props => props.isExpanded ? '0' : '0 auto'};
  border-radius: 14px;
  padding: ${props => props.isExpanded ? '0.25rem' : '0'};
  background: ${props => props.isExpanded
    ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.04) 0%, rgba(255, 255, 255, 0.01) 100%)'
    : 'transparent'};
  border: ${props => props.isExpanded ? '1px solid rgba(255, 255, 255, 0.08)' : 'none'};
  box-shadow: ${props => props.isExpanded
    ? 'inset 0 1px 0 rgba(255, 255, 255, 0.06), 0 8px 24px rgba(0, 0, 0, 0.22)'
    : 'none'};
  transition: all 0.3s ease;
`;

const UserAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary-color) 0%, rgba(255, 255, 255, 0.35) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-weight: 700;
  font-size: 0.875rem;
  flex-shrink: 0;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.32),
              0 0 0 2px var(--primary-rgba-35),
              inset 0 1px 0 rgba(255, 255, 255, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.24);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;

  /* Subtle glow ring */
  &::before {
    content: '';
    position: absolute;
    inset: -3px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--primary-rgba-20) 0%, transparent 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: -1;
  }

  img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
  }
`;

const UserButton = styled(motion.button)`
  display: flex;
  align-items: center;
  min-height: 52px;
  width: 100%;
  padding: ${props => props.isExpanded ? '0.85rem 0.75rem' : '0.75rem 0'};
  background: ${props => props.isExpanded
    ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, transparent 100%)'
    : 'none'};
  border: 1px solid transparent;
  cursor: pointer;
  border-radius: ${props => props.isExpanded ? '8px' : '12px'};
  justify-content: ${props => props.isExpanded ? 'flex-start' : 'center'};
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  gap: 0.75rem;
  color: rgba(255, 255, 255, 0.95);
  position: relative;

  &:hover {
    background: ${props => props.isExpanded
      ? 'linear-gradient(135deg, var(--primary-rgba-12) 0%, rgba(255, 255, 255, 0.04) 100%)'
      : 'var(--primary-rgba-10)'};
    border-color: ${props => props.isExpanded ? 'var(--primary-rgba-25)' : 'transparent'};
    transform: ${props => props.isExpanded ? 'none' : 'scale(1.05)'};
    box-shadow: ${props => props.isExpanded ? '0 10px 24px rgba(0, 0, 0, 0.25)' : 'none'};

    ${UserAvatar} {
      transform: scale(1.1);
      box-shadow: 0 6px 16px var(--primary-rgba-40),
                  0 0 0 2px var(--primary-rgba-30),
                  inset 0 1px 0 rgba(255, 255, 255, 0.4);

      &::before {
        opacity: 1;
      }
    }
  }

  &:active {
    transform: scale(0.98);
  }
`;

const UserInfo = styled.div`
  display: ${props => props.isExpanded ? 'flex' : 'none'};
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  flex: 1;
  min-width: 0;
  gap: 2px;
`;

const UserName = styled.div`
  font-weight: 600;
  font-size: 0.875rem;
  line-height: 1.2;
  color: var(--text-primary-dark);
  letter-spacing: 0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
`;

const UserRole = styled.div`
  font-size: 0.7rem;
  line-height: 1.2;
  color: rgba(255, 255, 255, 0.72);
  white-space: nowrap;
  text-transform: capitalize;
  margin-top: 0;
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  border: 1px solid var(--primary-rgba-25);
  background: var(--primary-rgba-10);
`;

const UserDropdownMenu = styled(motion.div)`
  position: absolute;
  bottom: calc(100% + 0.5rem);
  left: 0;
  right: 0;
  background: rgba(15, 15, 15, 0.98);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  min-width: 200px;
  overflow: hidden;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const UserDropdownItem = styled(Link)`
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  color: rgba(255, 255, 255, 0.95);
  text-decoration: none;
  transition: all 0.3s ease;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  
  svg {
    margin-right: 0.75rem;
    font-size: 1rem;
    color: inherit;
  }
  
  &:hover {
    background: var(--primary-rgba-10);
    color: var(--primary-color);
    
    svg {
      color: var(--primary-color);
    }
  }
`;

const UserLogoutButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  text-align: left;
  padding: 0.75rem 1rem;
  color: #ff6b78;
  background: linear-gradient(135deg, rgba(220, 53, 69, 0.12) 0%, rgba(220, 53, 69, 0.08) 100%);
  border: 1px solid rgba(220, 53, 69, 0.28);
  cursor: pointer;
  transition: all 0.25s ease;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  position: relative;
  overflow: hidden;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
  
  svg {
    margin-right: 0.75rem;
    font-size: 1rem;
    color: inherit;
    transition: transform 0.25s ease;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(120deg, transparent 20%, rgba(255, 255, 255, 0.18) 50%, transparent 80%);
    transform: translateX(-120%);
    transition: transform 0.55s ease;
  }
  
  &:hover {
    background: linear-gradient(135deg, rgba(220, 53, 69, 0.2) 0%, rgba(220, 53, 69, 0.12) 100%);
    border-color: rgba(220, 53, 69, 0.45);
    color: #ff8a95;
    transform: translateY(-1px);
    box-shadow: 0 8px 18px rgba(220, 53, 69, 0.2),
                inset 0 1px 0 rgba(255, 255, 255, 0.1);

    svg {
      transform: translateX(2px) scale(1.05);
    }

    &::after {
      transform: translateX(120%);
    }
  }

  &:active {
    transform: translateY(0);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08);
  }
`;

const UserDivider = styled.div`
  height: 1px;
  background-color: rgba(255, 255, 255, 0.1);
  margin: 0.25rem 0;
`;

const dropdownVariants = {
  hidden: { 
    opacity: 0, 
    y: 10,
    transition: {
      duration: 0.2
    }
  },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.3
    }
  }
};

const isExpensesPath = (pathname) =>
  pathname === '/expenses' ||
  pathname.startsWith('/expenses/') ||
  /^\/branches\/[^/]+\/expenses\/?$/.test(pathname);

const isNavItemActive = (item, pathname) => {
  if (item.path === '/expenses') return isExpensesPath(pathname);
  if (item.path === '/branches') return pathname === '/branches';
  return pathname === item.path || pathname.startsWith(`${item.path}/`);
};

const Sidebar = ({
  isExpanded,
  toggleSidebar,
  hideSidebar,
  deviceType = 'desktop',
  showToggleButton = true,
  onNavItemClick
}) => {
  const { currentUser, logout, needsOnboarding } = useContext(AuthContext);
  const { theme: themeContext } = useContext(ThemeContext);
  const { t } = useTranslation();
  const location = useLocation();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [hoveredNavItem, setHoveredNavItem] = useState(null);
  const [isAtPageTop, setIsAtPageTop] = useState(true);
  const userMenuRef = useRef(null);
  const sidebarContainerRef = useRef(null);
  
  // Get the actual theme object
  const theme = themeContext || {};
  
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsAtPageTop(window.scrollY <= 0);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
  };
  
  // Get user initials for avatar
  const getUserInitials = () => {
    if (!currentUser || !currentUser.firstName) return '?';
    return `${currentUser.firstName.charAt(0)}${currentUser.lastName ? currentUser.lastName.charAt(0) : ''}`;
  };
  
  // Format user role for display
  const formatRole = (role) => {
    if (!role) return '';
    return role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };
  
  const hasBranchSpecificPermissions = () => {
    if (!currentUser || !currentUser.branchPermissions) return false;
    
    const hasMenuPermissions = Array.isArray(currentUser.branchPermissions.menu) && 
                              currentUser.branchPermissions.menu.length > 0;
                              
    const hasTablesPermissions = Array.isArray(currentUser.branchPermissions.tables) && 
                                currentUser.branchPermissions.tables.length > 0;
                                
    return hasMenuPermissions || hasTablesPermissions;
  };
  
  const getNavItems = () => {
    if (!currentUser) return [];
    const navItems = [];

    if (isChef(currentUser) || isWaiter(currentUser)) {
      navItems.push({
        path: currentUser.branchId ? `/pos/${currentUser.branchId}` : '/pos',
        label: t('nav.posSystem'),
        icon: <FaCashRegister />,
        section: 'main'
      });
      if (isWaiter(currentUser)) {
        navItems.push({
          path: '/my-orders',
          label: t('nav.myOrders') || 'My Orders',
          icon: <FaClipboardList />,
          section: 'main'
        });
      }
      navItems.push({
        path: '/settings/themes',
        label: t('nav.colorThemes'),
        icon: <FaPalette />,
        section: 'main'
      });
      navItems.push({
        path: '/settings',
        label: t('common.settings'),
        icon: <FaCog />,
        section: 'main'
      });
      return filterNavItemsForElectron(navItems);
    }

    navItems.push({
      path: '/dashboard',
      label: t('nav.dashboard'),
      icon: <FaHome />,
      section: 'main'
    });

    if (hasBranchAdminAccess(currentUser)) {
      navItems.push({
        path: '/assistant',
        label: 'Sales Assistant',
        icon: <FaComments />,
        section: 'main'
      });
    }

    if (canAccessManageRestaurant(currentUser)) {
      navItems.push({
        path: '/manage-restaurant',
        label: t('nav.manageRestaurant'),
        icon: <FaUtensils />,
        section: 'main'
      });
    }

    if (canAccessBranches(currentUser)) {
      navItems.push({
        path: '/branches',
        label: t('nav.branches'),
        icon: <FaStore />,
        section: 'main'
      });
    }

    if (canCreateUsers(currentUser)) {
      navItems.push({
        path: '/users',
        label: t('nav.staff'),
        icon: <FaUsers />,
        section: 'main'
      });
    }

    if (canAccessPOS(currentUser)) {
      navItems.push({
        path: '/pos',
        label: t('nav.posSystem'),
        icon: <FaCashRegister />,
        section: 'main'
      });
    }

    if (hasBranchAdminAccess(currentUser)) {
      navItems.push({
        path: '/orders-history',
        label: t('nav.ordersHistory') || 'Orders History',
        icon: <FaClipboardList />,
        section: 'main'
      });
      navItems.push({
        path: '/expenses',
        label: t('nav.expenses') || 'Expenses',
        icon: <FaReceipt />,
        section: 'main'
      });
    }

    if (hasFullAccess(currentUser)) {
      navItems.push({
        path: '/invoices',
        label: 'Invoices',
        icon: <FaFileInvoice />,
        section: 'main'
      });
    }

    if (currentUser.role === 'platform_admin') {
      navItems.push({
        path: '/admin',
        label: 'Admin Portal',
        icon: <FaShieldAlt />,
        section: 'main'
      });
    }

    if (!hasFullAccess(currentUser) && canAccessPOS(currentUser)) {
      navItems.push({
        path: '/my-orders',
        label: t('nav.myOrders') || 'My Orders',
        icon: <FaUser />,
        section: 'main'
      });
    }

    if (canAccessQRMenu(currentUser)) {
      navItems.push({
        path: '/qr-menu',
        label: t('nav.qrMenu'),
        icon: <FaQrcode />,
        section: 'main'
      });
    }

    if (hasBranchSpecificPermissions()) {
      if (currentUser.branchPermissions?.menu && currentUser.branchPermissions.menu.length > 0) {
        navItems.push({
          path: '/assigned-menus',
          label: t('nav.branchMenus'),
          icon: <FaClipboardList />,
          section: 'branch-specific'
        });
      }
      if (currentUser.branchPermissions?.tables && currentUser.branchPermissions.tables.length > 0) {
        navItems.push({
          path: '/assigned-tables',
          label: t('nav.branchTables'),
          icon: <FaChair />,
          section: 'branch-specific'
        });
      }
    }

    navItems.push({
      path: '/settings/themes',
      label: t('nav.colorThemes'),
      icon: <FaPalette />,
      section: 'main'
    });
    navItems.push({
      path: '/settings',
      label: t('common.settings'),
      icon: <FaCog />,
      section: 'main'
    });
    // Downloads tab — only shown to owners/admins/branch_admins who can download the desktop app
    if (hasBranchAdminAccess(currentUser) && !isElectronApp()) {
      navItems.push({
        path: '/downloads',
        label: 'Downloads',
        icon: <FaDownload />,
        section: 'main'
      });
    }
    return filterNavItemsForElectron(navItems);
  };
  
  const groupedNavItems = () => {
    const items = getNavItems();
    const grouped = {};
    
    items.forEach(item => {
      if (!grouped[item.section]) {
        grouped[item.section] = [];
      }
      grouped[item.section].push(item);
    });
    
    return grouped;
  };
  
  const navItemGroups = groupedNavItems();

  const handleNavItemClick = () => {
    setIsUserMenuOpen(false);

    if ((deviceType === 'mobile' || deviceType === 'tablet') && typeof onNavItemClick === 'function') {
      onNavItemClick();
    }
  };

  // Animation variants - Sidebar appears LAST with fade-in only
  const sidebarContainerVariants = {
    hidden: { 
      opacity: 0
    },
    visible: { 
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: [0.4, 0, 0.2, 1],
        delay: 0.9,
        when: "beforeChildren",
        staggerChildren: 0.03,
        delayChildren: 0.05
      }
    }
  };

  const navItemVariants = {
    hidden: { 
      opacity: 0
    },
    visible: { 
      opacity: 1,
      transition: {
        duration: 0.4,
        ease: [0.4, 0, 0.2, 1]
      }
    }
  };

  const sectionTitleVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: {
        duration: 0.4,
        ease: [0.4, 0, 0.2, 1]
      }
    }
  };
  
  return (
    <SidebarContainer
      ref={sidebarContainerRef}
      initial="hidden"
      animate="visible"
      variants={sidebarContainerVariants}
      theme={theme}
      isExpanded={isExpanded}
      isAtPageTop={isAtPageTop}
    >
      <NavGroupsLock disabled={needsOnboarding}>
      <NavSection isExpanded={isExpanded}>
        <NavSectionTitle
          isExpanded={isExpanded}
          variants={sectionTitleVariants}
          initial="hidden"
          animate="visible"
          theme={theme}
        >
          {t('nav.main')}
        </NavSectionTitle>

        {navItemGroups.main && navItemGroups.main.map((item, index) => (
          <NavItemWrapper
            key={item.path}
            onMouseEnter={() => !isExpanded && setHoveredNavItem(item.path)}
            onMouseLeave={() => !isExpanded && setHoveredNavItem(null)}
          >
            <NavItem
              to={item.path}
              end={item.path === '/branches'}
              isExpanded={isExpanded}
              onClick={handleNavItemClick}
              className={isNavItemActive(item, location.pathname) ? 'active' : ''}
              variants={navItemVariants}
              initial="hidden"
              animate="visible"
              custom={index}
              theme={theme}
            >
              {item.icon}
              <span>{item.label}</span>
            </NavItem>
            {!isExpanded && (
              <AnimatePresence>
                {hoveredNavItem === item.path && (
                  <NavItemTooltip
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    variants={tooltipVariants}
                  >
                    {item.label}
                  </NavItemTooltip>
                )}
              </AnimatePresence>
            )}
          </NavItemWrapper>
        ))}
      </NavSection>
      
      {navItemGroups['branch-specific'] && navItemGroups['branch-specific'].length > 0 && (
        <NavSection isExpanded={isExpanded}>
          <NavSectionTitle 
            isExpanded={isExpanded}
            variants={sectionTitleVariants}
            initial="hidden"
            animate="visible"
            theme={theme}
          >
            {t('nav.branchAccess')}
          </NavSectionTitle>

          {navItemGroups['branch-specific'].map((item, index) => (
            <NavItemWrapper
              key={item.path}
              onMouseEnter={() => !isExpanded && setHoveredNavItem(item.path)}
              onMouseLeave={() => !isExpanded && setHoveredNavItem(null)}
            >
              <NavItem
                to={item.path}
                isExpanded={isExpanded}
                onClick={handleNavItemClick}
                className={({ isActive }) => isActive ? 'active' : ''}
                variants={navItemVariants}
                initial="hidden"
                animate="visible"
                custom={index}
                theme={theme}
              >
                {item.icon}
                <span>{item.label}</span>
              </NavItem>
              {!isExpanded && (
                <AnimatePresence>
                  {hoveredNavItem === item.path && (
                    <NavItemTooltip
                      initial="hidden"
                      animate="visible"
                      exit="hidden"
                      variants={tooltipVariants}
                    >
                      {item.label}
                    </NavItemTooltip>
                  )}
                </AnimatePresence>
              )}
            </NavItemWrapper>
          ))}
        </NavSection>
      )}
      </NavGroupsLock>

      {/* User Menu Footer */}
      <SidebarFooter isExpanded={isExpanded}>
        <UserSection ref={userMenuRef} isExpanded={isExpanded} theme={theme}>
          <UserButton 
            isExpanded={isExpanded}
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            theme={theme}
          >
            <UserAvatar theme={theme}>
              {currentUser?.avatarUrl ? (
                <img src={currentUser.avatarUrl} alt={currentUser.firstName} />
              ) : (
                getUserInitials()
              )}
            </UserAvatar>
            {isExpanded && (
              <UserInfo isExpanded={isExpanded}>
                <UserName theme={theme}>
                  {currentUser?.firstName} {currentUser?.lastName}
                </UserName>
                <UserRole theme={theme}>
                  {formatRole(currentUser?.role)}
                </UserRole>
              </UserInfo>
            )}
          </UserButton>
          
          <AnimatePresence>
            {isUserMenuOpen && isExpanded && (
              <UserDropdownMenu
                initial="hidden"
                animate="visible"
                exit="hidden"
                variants={dropdownVariants}
                theme={theme}
              >
                <UserDropdownItem to="/profile" theme={theme}>
                  <FaUser />
                  {t('common.myProfile')}
                </UserDropdownItem>

                {!isElectronApp() && (
                  <UserDropdownItem to="/settings" theme={theme}>
                    <FaCog />
                    {t('common.settings')}
                  </UserDropdownItem>
                )}

                <UserDivider theme={theme} />
                
                <UserLogoutButton onClick={handleLogout} theme={theme}>
                  <FaSignOutAlt />
                  {t('common.signOut')}
                </UserLogoutButton>
              </UserDropdownMenu>
            )}
          </AnimatePresence>
        </UserSection>
      </SidebarFooter>
      
    </SidebarContainer>
  );
};

export default Sidebar;
